import { useAuth0 } from "@auth0/auth0-react"
import { clearAllFeatureFlags, isPixiesetAuthEnabled } from "./measurement/feature-flag"
import {
	isRefreshingTokenAtom,
	getPSAccessToken,
	loginToPS,
	isPSAuthenticatedAtom,
	logoutFromPS,
	type PSAccessTokenRequestContext,
} from "@/api/OAuthClient"
import { useAtomValue } from "jotai"
import { workspaceIdAtom } from "@/store/Pixieset/pixieset-state"
import { store } from "@/store/store"
import { useCallback } from "react"
import { getAppUrl, getCustomProtocol, isElectronApp } from "./electron"

/**
 * Returns the current SPA URL, or a deep link into Electron
 */
export function getRedirectURI(options?: { legacy?: boolean }): string {
	if (!isElectronApp) {
		return window.location.origin
	}

	// Legacy Auth0 flow still uses the app-oauth intermediate page
	if (options?.legacy) {
		const url = new URL(window.location.origin)
		url.pathname = "/app-oauth"
		url.searchParams.set("legacy", "1")
		return url.toString()
	}

	// Pixieset auth: use custom protocol directly
	// e.g. com.pixieset.editor://editor.pixieset.com
	// Uses the Electron-configured APP_URL host (not window.location) so the redirect URI
	// matches what's registered with the OAuth server. This means auth only works for
	// staging (editor.nextshutter.com) and production (editor.pixieset.com), not preview apps.
	const appUrl = getAppUrl()
	const protocol = getCustomProtocol()
	if (!appUrl || !protocol) {
		// Older shells don't expose appUrl/customProtocol — fall back to web redirect.
		return window.location.origin
	}
	const { host } = new URL(appUrl)
	return `${protocol}://${host}`
}

export function useAuth() {
	const {
		isAuthenticated: isAuth0Authenticated,
		isLoading: isAuth0Loading,
		getAccessTokenSilently,
		loginWithRedirect: loginAuth0WithRedirect,
		logout: logoutAuth0,
	} = useAuth0()

	const isPSAuthenticated = useAtomValue(isPSAuthenticatedAtom)
	const isRefreshingToken = useAtomValue(isRefreshingTokenAtom)
	const isAuthenticated = isPixiesetAuthEnabled() ? isPSAuthenticated : isAuth0Authenticated
	const isLoading = isPixiesetAuthEnabled()
		? isRefreshingToken
		: isRefreshingToken || isAuth0Loading
	const loginWithRedirect = isPixiesetAuthEnabled() ? loginToPS : loginAuth0WithRedirect

	const getAccessToken = useCallback(
		(request?: PSAccessTokenRequestContext) => {
			if (isPixiesetAuthEnabled()) {
				return getPSAccessToken(request)
			} else {
				return getAccessTokenSilently()
			}
		},
		[getAccessTokenSilently],
	)

	const isURLAuthenticated = useCallback(
		(url: URL) => {
			if (isPixiesetAuthEnabled(url)) {
				return isPSAuthenticated
			} else {
				return isAuth0Authenticated
			}
		},
		[isPSAuthenticated, isAuth0Authenticated],
	)

	const logout = useCallback(() => {
		// Clear feature flags on logout.
		// Concerned in Electron: if we use pre-defined feature flags for testing, shall they persist after logout?
		clearAllFeatureFlags()

		if (isPixiesetAuthEnabled()) {
			store.set(workspaceIdAtom, null)

			return logoutFromPS()
		} else {
			return logoutAuth0({
				logoutParams: {
					returnTo: window.location.origin,
				},
			})
		}
	}, [logoutAuth0])

	return {
		isAuthenticated,
		isLoading,
		getAccessToken,
		logout,
		loginWithRedirect,
		isURLAuthenticated,
	}
}
